import { Component, LOCALE_ID, HostListener, inject, signal, ViewEncapsulation, OnDestroy, AfterViewInit, computed } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from './core/auth/auth.service';
import { CustomIconService } from './core/services/custom-icon.service';
import { DatadogService } from './shared/services/datadog.service';
import { AppState } from 'src/app/app.state';
import { AsyncPipe } from '@angular/common';
import { environment } from 'src/environments/environment';
import { Subscription, filter, map, combineLatest, of, catchError } from 'rxjs';
import { EnvironmentLoaderService } from './shared/services/environment-loader.service';
import { WebServiceWorker } from './core/services/web-service-worker';
import { SnackMessageService } from './shared/services/snack-message.service';
import { ConfigService } from './shared/services/config.service';
import { DarkModeService, MenuItem, NavigationMenuComponent } from 'ok-angular-shared/components';
import { RouterModule } from '@angular/router';
import { AppMenuItem } from './app.interface';
import { Permission } from './shared/enums/permission.enum';
import { QuickSearchOverlayComponent } from './shared/components/quick-search-overlay/quick-search-overlay.component';
import { FeatureFlagService } from './shared/services/feature-flag.service';
import { FEATURE_FLAGS } from './shared/services/feature-flags.const';

@Component({
  selector: 'adm-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [RouterOutlet, NavigationMenuComponent, RouterModule, AsyncPipe],
})
export class AppComponent implements AfterViewInit, OnDestroy {
  localeId = inject(LOCALE_ID);
  private router = inject(Router);
  private customIconService = inject(CustomIconService);
  private environmentLoaderService = inject(EnvironmentLoaderService);
  private authService = inject(AuthService);
  private dataDogService = inject(DatadogService);
  private webServiceWorker = inject(WebServiceWorker);
  private snackMessageService = inject(SnackMessageService);
  private configService = inject(ConfigService);
  private dialog = inject(MatDialog);
  private darkModeService = inject(DarkModeService);
  private featureFlagService = inject(FeatureFlagService);

  appState = inject(AppState);

  /**
   * 📋 Main navigation menu items
   */
  public menuItems = signal<AppMenuItem[]>([]);
  public bottomMenuItems = signal<MenuItem[]>([]);

  public roles = this.environmentLoaderService.virtualRoles;

  private servicesAreInitiated = false;
  public version: string = environment.version;
  public userName: string | null = null;
  public enableDarkMode$ = of(false); // Hardcoded to false as part of phasing out Launch Darkly.
  public enableLiveCharging$ = of(false); // Hardcoded to false as part of phasing out Launch Darkly.
  public enablePriceRegulations$ = this.featureFlagService.getFeatureFlag(FEATURE_FLAGS.PRICE_REGULATIONS_READ).pipe(
    map(flag => flag.result),
    catchError(() => of(false))
  );

  public searchIsExpandedChangedSubscription?: Subscription;
  public enableLiveChargingSubscription?: Subscription;
  public enableDarkModeSubscription?: Subscription;
  languages = [
    { code: 'da', url: 'da', label: 'Dansk' },
    { code: 'en-US', url: 'en', label: 'English' },
    { code: 'de', url: 'de', label: 'Deutsch' },
  ];

  public ngAfterViewInit(): void {
    // Subscribe to both feature flags using combineLatest to handle them properly together
    this.enableLiveChargingSubscription = combineLatest([
      this.enableLiveCharging$,
      this.enableDarkMode$,
      this.enablePriceRegulations$,
    ]).subscribe(([isLiveChargingEnabled, isDarkModeEnabled, isPriceRegulationsEnabled]) => {
      this.updateMenuItems(isLiveChargingEnabled, isDarkModeEnabled, isPriceRegulationsEnabled);
    });
  }

  private updateMenuItems(isLiveChargingEnabled: boolean, isDarkModeEnabled: boolean, isPriceRegulationsEnabled: boolean): void {
    const priceChildren: AppMenuItem[] = [
      {
        title: 'Prispolitikker',
        route: '/price-policies',
        svgIcon: 'sell_outlined',
        reverseDirection: true,
        requiredPermissions: [Permission.PricePolicyRead],
      },
    ];

    if (isPriceRegulationsEnabled) {
      priceChildren.push({
        title: 'Prisreguleringer',
        route: '/price-regulations',
        svgIcon: 'price_change',
        reverseDirection: true,
        requiredPermissions: [Permission.PriceRegulationRead],
      });
    }

    const baseMenuItems: AppMenuItem[] = [
      {
        title: 'Portal søgning',
        icon: 'search',
        action: () => this.openSearchOverlay(),
        requiredPermissions: [Permission.QuickSearchRead],
      },
      {
        title: 'OK-app emulator',
        route: '/ok-app-emulator',
        requiredPermissions: [Permission.OkAppEmulator],
      },
    ];

    // Conditionally add Live ladninger if feature flag is enabled
    if (isLiveChargingEnabled) {
      baseMenuItems.push({
        title: 'Live ladninger',
        route: '/live-charging-dashboard',
        requiredPermissions: [Permission.OkAppEmulator],
      });
    }

    // Add the rest of the menu items
    const remainingMenuItems: AppMenuItem[] = [
      {
        title: 'Organisering',
        shouldAutoNavigateToFirstChildRoute: true,
        requiredPermissions: [Permission.OrganizationRead, Permission.ChargingStationLocationRead],
        children: [
          {
            title: 'Organisationer',
            route: '/organizations',
            icon: { name: 'building-s' },
            requiredPermissions: [Permission.OrganizationRead],
            reverseDirection: true,
          },
          {
            title: 'Lokationer',
            route: '/locations',
            icon: { name: 'location-s' },
            requiredPermissions: [Permission.ChargingStationLocationRead],
            reverseDirection: true,
          },
        ],
      },
      {
        title: 'Adgangsstyring',
        shouldAutoNavigateToFirstChildRoute: true,
        requiredPermissions: [Permission.IdTagRead, Permission.ChargingUserRead, Permission.AccessGroupRead],
        children: [
          {
            title: 'Brikker',
            route: '/id-tags',
            icon: { name: 'token-s' },
            requiredPermissions: [Permission.IdTagRead],
            reverseDirection: true,
          },
          {
            title: 'Brugere',
            route: '/charging-users',
            icon: { name: 'user-m' },
            requiredPermissions: [Permission.ChargingUserRead],
            reverseDirection: true,
          },
          {
            title: 'Adgangsgrupper',
            route: '/access-groups',
            icon: { name: 'users-m' },
            requiredPermissions: [Permission.AccessGroupRead],
            reverseDirection: true,
          },
        ],
      },
      {
        title: 'Smart-enheder',
        shouldAutoNavigateToFirstChildRoute: true,
        requiredPermissions: [Permission.PayterPaymentTerminalRead, Permission.PriceSignRead],
        children: [
          {
            title: 'Betalingsterminaler',
            route: '/payment-terminals/payter',
            icon: 'point_of_sale',
            reverseDirection: true,
            requiredPermissions: [Permission.PayterPaymentTerminalRead],
          },
          {
            title: 'Prisskilte',
            route: '/price-sign',
            icon: 'signpost',
            reverseDirection: true,
            requiredPermissions: [Permission.PriceSignRead],
          },
        ],
      },
      {
        title: 'Priser',
        shouldAutoNavigateToFirstChildRoute: true,
        requiredPermissions: [Permission.PricePolicyRead, Permission.PriceRegulationRead],
        children: priceChildren,
      },
      {
        title: 'Portalstyring',
        shouldAutoNavigateToFirstChildRoute: true,
        requiredPermissions: [Permission.ModuleRead, Permission.UserRead],
        children: [
          {
            title: 'Moduler',
            route: '/modules',
            icon: 'calendar_view_month',
            reverseDirection: true,
            requiredPermissions: [Permission.ModuleRead],
          },
          {
            title: 'Portalbrugere',
            route: '/users',
            icon: 'assignment_ind',
            reverseDirection: true,
            requiredPermissions: [Permission.UserRead],
          },
        ],
      },
      {
        title: 'Driftstatus',
        route: '/incidents',
        requiredPermissions: [Permission.IncidentRead],
      },
    ];

    // Finally, filter menu items based on user permissions and update the signal
    this.menuItems.set(this.filterMenuItemsByPermissions([...baseMenuItems, ...remainingMenuItems]));

    // Update bottom menu items based on feature flags
    this.updateBottomMenuItems(isDarkModeEnabled);
  }

  private updateBottomMenuItems(isDarkModeEnabled: boolean): void {
    const bottomItems: MenuItem[] = [];

    // Conditionally add dark mode toggle if feature flag is enabled
    if (isDarkModeEnabled) {
      bottomItems.push({
        icon: computed(() => {
          return this.darkModeService.isDarkMode() ? 'light_mode' : 'dark_mode';
        }),
        title: computed(() => {
          return this.darkModeService.isDarkMode() ? 'Lyst tema' : 'Mørkt tema';
        }),
        action: () => this.darkModeService.toggleDarkMode(),
      });
    }

    // Always add logout button
    bottomItems.push({
      title: 'Log ud',
      icon: 'logout',
      action: () => this.logout(),
    });

    this.bottomMenuItems.set(bottomItems);
  }

  title = 'client';
  baseUrl: string;
  @HostListener('window:keydown.control.s', ['$event']) activateQuickSearch(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    keyboardEvent.preventDefault();
    this.openSearchOverlay();
  }

  constructor() {
    this.baseUrl = window.location.origin;
    combineLatest(this.authService.isAuthenticated$, this.configService.config$).subscribe(([isAuthenticated, config]) => {
      if (isAuthenticated) {
        if (!this.userHasValidRoles(config)) {
          this.appState.isAuthenticated$.next(false);
          this.router.navigateByUrl('/logout');
        } else {
          this.appState.isAuthenticated$.next(isAuthenticated);
        }

        if (!this.servicesAreInitiated) {
          this.customIconService.init();
          this.authService.preferredUsername.then(u => {
            this.userName = u;
          });
          this.dataDogService.init();
          this.servicesAreInitiated = true;
        }
      }
    });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map((e: NavigationEnd) => e.urlAfterRedirects)
      )
      .subscribe(() => {
        this.appState.cssVarListUnitHeaderTranslateX = 0;
        this.appState.cssVarListUnitHeaderTranslateY = 0;
      });
  }

  userHasValidRoles(config: unknown): boolean {
    const portalRolesObject = config as Record<string, string>;
    const virtualRoles = Object.values(portalRolesObject ?? {});
    const distinctRoleNames: string[] = [];
    virtualRoles.forEach((roleNames: string) => {
      roleNames.split(',').forEach(roleName => {
        if (!distinctRoleNames.includes(roleName)) {
          distinctRoleNames.push(roleName);
        }
      });
    });
    return distinctRoleNames.some(rolesForPermission => this.authService.roles?.includes(rolesForPermission));
  }

  logout() {
    this.authService.logout();
  }

  getLanguage(localeId: string) {
    const language = this.languages.find(x => x.url === localeId);
    return language ? language.url.toUpperCase() : 'DA';
  }

  navigateByUrl(url: string) {
    location.href = '/' + url + '/' + this.getCurrentRoute();
  }

  getCurrentRoute() {
    return this.router.url;
  }

  openSearchOverlay() {
    this.dialog.open(QuickSearchOverlayComponent, {
      panelClass: 'quick-search-dialog',
      disableClose: false,
    });
  }

  ngOnDestroy(): void {
    // Clean up subscriptions to prevent memory leaks
    this.searchIsExpandedChangedSubscription?.unsubscribe();
    this.enableLiveChargingSubscription?.unsubscribe();
    this.enableDarkModeSubscription?.unsubscribe();
  }

  private filterMenuItemsByPermissions(menuItems: AppMenuItem[]): AppMenuItem[] {
    return menuItems.filter(menuItem => {
      if (menuItem.children) {
        menuItem.children = this.filterMenuItemsByPermissions(menuItem.children);
        if (menuItem.children.length === 0) {
          return false;
        }
      }
      return (
        menuItem.requiredPermissions?.some(permission =>
          this.authService.hasRole(this.environmentLoaderService.virtualRoles[permission])
        ) ?? false
      );
    });
  }
}
